/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import * as jwt_decode from 'jwt-decode';

/**
 * Manages JWT tokens for customer API and data access.
 * Breaks the circular dependency between AuthService and services that only need auth headers.
 * Services that need authentication can inject TokenService instead of the full AuthService.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenService {
  /** Customer API JWT access token */
  token: string;

  /** Data access JWT token for data-related APIs */
  tokenDataAccess: string;

  /** Whether JWT authorization has been verified */
  jwtAuthorized = false;

  /** Whether the ET-token probe has already run this SPA session */
  etTokenProbed = false;

  /** Whether to use localStorage for token persistence */
  private useStorage = false;

  /** Whether the user is in a reader/widget context (uses in-memory token instead of localStorage) */
  private isReaderContext = false;

  /**
   * Whether this tab holds an impersonated session. Persisted per-tab in
   * sessionStorage so the impersonated token never shares the cross-tab
   * localStorage `token` key with the admin's own session — which otherwise
   * clobbers it and triggers the cross-tab logout every refresh cycle.
   */
  private isImpersonating = false;

  /**
   * Configure storage behavior. Called once during app initialization.
   */
  configure(options: { useStorage: boolean; isReaderContext: boolean }): void {
    this.useStorage = options.useStorage;
    this.isReaderContext = options.isReaderContext;
    // Restore the per-tab impersonation flag across a reload of this tab.
    try {
      this.isImpersonating = sessionStorage.getItem('impersonating') === '1';
    } catch {
      this.isImpersonating = false;
    }
  }

  /** Whether this tab holds an impersonated (per-tab, sessionStorage) session. */
  get impersonating(): boolean {
    return this.isImpersonating;
  }

  /**
   * Mark this tab as an impersonated session (or clear it). Called by the auth
   * guard when it processes the `impersonate` query param.
   */
  setImpersonating(value: boolean): void {
    this.isImpersonating = value;
    try {
      if (value) {
        sessionStorage.setItem('impersonating', '1');
      } else {
        sessionStorage.removeItem('impersonating');
      }
    } catch {
      /* sessionStorage unavailable — flag stays in memory for this tab */
    }
  }

  /**
   * The store the current session persists to: sessionStorage while
   * impersonating (per-tab), localStorage otherwise, or null for a
   * reader/widget context (memory-only) or when storage is disabled.
   */
  private persistenceStore(): Storage | null {
    if (this.isReaderContext) return null;
    if (this.isImpersonating) return sessionStorage;
    return this.useStorage ? localStorage : null;
  }

  /**
   * Store tokens and persist them to the appropriate per-context store.
   */
  setTokens(token: string, dataAccessToken?: string): void {
    this.token = token;
    if (dataAccessToken) {
      this.tokenDataAccess = dataAccessToken;
    }
    this.persistTokens();
  }

  /**
   * Persist the current in-memory tokens to the appropriate store. Callers set
   * `token`/`tokenDataAccess` first, then call this.
   */
  persistTokens(): void {
    const store = this.persistenceStore();
    if (!store) return;
    try {
      if (this.token) store.setItem('token', this.token);
      if (this.tokenDataAccess) {
        store.setItem('tokenDataAccess', this.tokenDataAccess);
      }
    } catch {
      /* storage unavailable (private mode / quota) — token stays in memory */
    }
  }

  /**
   * Get the current active token (from memory or the per-context store).
   */
  getActiveToken(): string | null {
    if (this.isReaderContext) {
      return this.token;
    }
    // Read from whichever store applies (per-tab session while impersonating,
    // else localStorage when enabled). Wrapped in try/catch because a bare
    // storage access can throw in private browsing / blocked-site-data modes.
    const store = this.persistenceStore();
    let stored: string | null = null;
    try {
      stored = store ? store.getItem('token') : null;
    } catch {
      stored = null;
    }
    return stored || this.token;
  }

  /**
   * Decode a JWT token.
   */
  decodeToken(token?: string): any {
    const t = token || this.token;
    if (!t) return null;
    return jwt_decode(t);
  }

  /**
   * Clear all auth state (called on logout and on a 401).
   */
  clear(): void {
    const wasImpersonating = this.isImpersonating;
    this.token = undefined;
    this.tokenDataAccess = undefined;
    this.jwtAuthorized = false;
    this.etTokenProbed = false;
    this.isImpersonating = false;
    // An impersonation tab must NOT touch the shared localStorage token — that is
    // the admin's session in another tab, and removing it fires the cross-tab
    // logout (newValue=null). It only owns its per-tab sessionStorage.
    if (!wasImpersonating && this.useStorage) {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenDataAccess');
      localStorage.removeItem('impersonate');
    }
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tokenDataAccess');
      sessionStorage.removeItem('impersonating');
    } catch {
      /* ignore */
    }
  }
}
